import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ToastProvider } from './components/Toast';
import NavigationModern from './components/NavigationModern';
import FooterModern from './components/FooterModern';
import LoginModern from './components/LoginModern';
import SignupModern from './components/SignupModern';
import Dashboard from './components/Dashboard';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import HomeModern from './pages/HomeModern';
import LawyerListing from './pages/LawyerListing';
import LawyerProfileModern from './pages/LawyerProfileModern';
import LawyerProfileSetup from './pages/LawyerProfileSetup';
import BookingModern from './pages/BookingModern';
import ContactModern from './pages/ContactModern';
import AdminDashboard from './pages/AdminDashboard';
import CaseStudiesPublic from './pages/CaseStudiesPublic';
import CaseStudies from './pages/CaseStudies';
import About from './pages/About';
import Blog from './pages/Blog';
import Team from './pages/Team';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('auth_token');
    const userData = localStorage.getItem('user_data');
    
    if (token && userData) {
      setIsAuthenticated(true);
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('user_data', JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    setIsAuthenticated(false);
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center animate-pulse">
            <span className="text-white text-xl">⚖️</span>
          </div>
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <ToastProvider>
    <Router>
      <div className="min-h-screen flex flex-col">
      <NavigationModern isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
      <main className="flex-1 pt-16">
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomeModern />} />
        <Route path="/lawyers" element={<LawyerListing user={user} />} />
        <Route path="/lawyer/:id" element={<LawyerProfileModern />} />
        <Route path="/booking" element={<BookingModern user={user} />} />
        <Route path="/contact" element={<ContactModern />} />
        <Route path="/about" element={<About />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/team" element={<Team />} />
        <Route path="/case-studies" element={<CaseStudiesPublic />} />
        <Route path="/case-study/:id" element={<CaseStudiesPublic />} />

        {/* Auth Routes */}
        <Route 
          path="/login" 
          element={<LoginModern onLogin={handleLogin} />}
        />
        <Route 
          path="/signup" 
          element={<SignupModern onLogin={handleLogin} />}
        />

        {/* Protected Routes */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <Dashboard user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/lawyer-profile-setup" 
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <LawyerProfileSetup user={user} />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/admin" 
          element={
            <AdminRoute isAuthenticated={isAuthenticated} user={user}>
              <AdminDashboard />
            </AdminRoute>
          }
        />
      </Routes>
      </main>
      <FooterModern />

      {/* Floating WhatsApp & Call Buttons */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3">
        <a
          href="tel:+917970809406"
          className="flex items-center justify-center w-14 h-14 rounded-full shadow-lg transition-transform hover:scale-110"
          style={{ background: 'linear-gradient(135deg, #1e3a5f, #2c5282)' }}
          title="Call Now"
          aria-label="Call Now"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        </a>
        <a
          href="https://wa.me/917970809406"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center w-14 h-14 rounded-full shadow-lg transition-transform hover:scale-110"
          style={{ background: '#25D366' }}
          title="Chat on WhatsApp"
          aria-label="Chat on WhatsApp"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </a>
      </div>
      </div>
    </Router>
    </ToastProvider>
  );
}

export default App;
